import {AfterViewInit, Component, ElementRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {ErrorService} from "../../../services/error.service";
import {UtilityService} from "../../../services/utility.service";
import {
  RecordData,
  RecordsDataRequestQueryParams,
  RecordTableColumnParams,
  RecordTableParams,
  RecordUpdateRequestData,
  RecordUpdateRequestParams
} from "../../../types/records-table.types";
import {defaultRecordsDataRequestQueryParams} from "../../../models/default-records-data-request-query-params";
import {Pageable} from "../../../types/pagable.types";
import {CloseModalReason} from "../../../types/modal.types";
import {MatDialogRef} from "@angular/material/dialog";
import {RepeatsModalService} from "../repeats-modal/services/repeats-modal.service";
import {DeviceService} from "../../../services/device.service";
import {Subscription} from "rxjs";
import {AuthService} from "../../../services/auth.service";
import {ModalsService} from "../../../services/modals.service";
import {CommonModule} from '@angular/common';
import {IconStatus} from "../../../models/icon-status-config";
import {ValidationService} from "../../../services/validation.service";
import {SnackbarService} from "../../../services/snackbar.service";
import {EditModeService} from "../../../services/edit-mode.service";
import {FieldClassProps, GetRecordPropertyDataByIdParams, ModalSize} from "./types/base-modal.types";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {FormGroup, FormsModule} from "@angular/forms";
import {LoadingService} from "../../../services/loading.service";
import {BaseModalService} from "../services/base-modal.service";
import {AttachProps, DetachProps} from "../../../types/attach-detach.types";
import {RecordTableApiService} from "../../../services-api/record-table-api.service";
import {BaseModalApiService} from "../services/base-modal-api.service";
import {MatIcon} from "@angular/material/icon";
import {MatMiniFabButton} from "@angular/material/button";
import {FullScreenStateService} from "../services/full-screen-state.service";
import {platformUrls, PlatformUrlType} from "../../../models/platform-urls-config";
import {ModalStatesService} from "../../../services/table-services/modal-states.service";

@Component({
  selector: 'app-base-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatSlideToggle,
    FormsModule,
    MatIcon,
    MatMiniFabButton
  ],
  templateUrl: './base-modal.component.html',
  styleUrl: './base-modal.component.scss'
})
export class BaseModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() modalName: string = '';
  @Input() title: string = 'Base modal title';
  @Input() size: ModalSize = 'large';
  @Input() showStateToggle: boolean = false;
  @Input() isNeedFullScreenBtn: boolean = false;
  @Input() modalMaxHeight: string = '';
  @Input() modalLargeMaxWidth: string = '';

  protected errorService: ErrorService = inject(ErrorService);
  protected recordTableApiService: RecordTableApiService = inject(RecordTableApiService);
  protected utilityService: UtilityService = inject(UtilityService);
  protected dialogRef: MatDialogRef<BaseModalComponent> = inject(MatDialogRef<BaseModalComponent>);
  protected repeatsModalService: RepeatsModalService = inject(RepeatsModalService);
  protected deviceService: DeviceService = inject(DeviceService);
  protected authService: AuthService = inject(AuthService);
  protected modalsService: ModalsService = inject(ModalsService);
  protected validationService: ValidationService = inject(ValidationService);
  protected snackbarService: SnackbarService = inject(SnackbarService);
  protected editModeService: EditModeService = inject(EditModeService);
  protected loadingService: LoadingService = inject(LoadingService);
  protected baseModalService: BaseModalService = inject(BaseModalService);
  protected modalService: ModalsService = inject(ModalsService);
  protected baseModalApiService: BaseModalApiService = inject(BaseModalApiService);
  protected el: ElementRef = inject(ElementRef);
  protected fullScreenStateService: FullScreenStateService = inject(FullScreenStateService);
  protected modalStatesService: ModalStatesService = inject(ModalStatesService);

  protected subscriptions: Subscription = new Subscription();
  protected isMobile: boolean = false;
  protected isEditModeActive: boolean = false;
  protected isLoading: boolean = false;

  public ngOnInit(): void {
    this.initSubscriptions();
  }

  public ngAfterViewInit(): void {
    this.modalMaxHeight && this.el.nativeElement.style.setProperty('--modal-max-height', this.modalMaxHeight);
    this.modalLargeMaxWidth && this.el.nativeElement.style.setProperty('--modal-large-max-width', this.modalLargeMaxWidth);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.editModeService.setEditMode(false);
    this.fullScreenStateService.resetFullScreenState();
  }

  protected initSubscriptions(): void {
    this.initEditModeSubscriptions();
    this.initLoadingSubscriptions();
  }

  protected handleStateToggleChange(isToggleActive: boolean): void {
    this.editModeService.setEditMode(isToggleActive);
  }

  protected handleFullScreen(): void {
    this.fullScreenStateService.toggleFullScreenState();
    this.fullScreenStateService.fullScreenState() ?
      this.dialogRef.addPanelClass('fullscreen-modal') :
      this.dialogRef.removePanelClass('fullscreen-modal');
  }

  protected getFullScreenIcon(): string {
    return this.fullScreenStateService.fullScreenState() ? 'fullscreen_exit' : 'fullscreen';
  }

  protected setFieldClasses(fieldClassProps: FieldClassProps = {}): string {
    const nonePointerEvents = !this.isEditModeActive ? 'none-pointer-events' : '';

    return this.utilityService.isFieldFullFilled(fieldClassProps) ? `${nonePointerEvents} 'full-filled'` : `${nonePointerEvents}`;
  }

  protected getFieldStyles(fieldClassProps: FieldClassProps): Record<string, string> {
    const { formControlName, formGroup } = fieldClassProps;

    const isActive = this.utilityService.isActiveLinkRowForUrlType(formGroup!.get(formControlName!)?.value, this.isEditModeActive);
    return {
      'cursor': isActive ? 'pointer' : 'default',
      'pointer-events': isActive ? 'auto' : ''
    };
  }

  protected handleValueClick(fieldClassProps: FieldClassProps): void {
    if (this.isEditModeActive) { return; }

    const { formControlName, formGroup } = fieldClassProps;

    if (!formControlName || !formGroup) { return; }

    const value = formGroup.get(formControlName)?.value.trim();
    window.open(value as string, '_blank')?.focus();
  }

  protected prepareDefaultQueryParams(): RecordsDataRequestQueryParams {
    return {
      ...defaultRecordsDataRequestQueryParams
    }
  }

  protected getFieldStyleCursor(key: string, formGroup: FormGroup): { [key: string]: string } {
    return {
      'cursor': this.isActiveLinkRowForUrlType(key) && formGroup.get(key)?.value ? 'pointer' : 'default'
    };
  }

  protected handleOpenUrlClick(key: string, formGroup: FormGroup): void {
    if (!this.isActiveLinkRowForUrlType(key)) { return; }

    if (key === 'phoneNumber') {
      this.baseModalService.initiateCall(key, formGroup);
      return;
    }

    this.baseModalService.openUrlInNewTab(key, formGroup);
  }

  protected fetchRecordData(path: string, queryParams: RecordsDataRequestQueryParams): Promise<Pageable<RecordData>> {
    return new Promise((resolve, reject) => {
      this.recordTableApiService.fetchRecordTableData(path, queryParams).subscribe({
        next: (recordsTableData: Pageable<RecordData>) => resolve(recordsTableData),
        error: (error) => reject(error)
      });
    });
  }

  protected addNewRecord(path: string, recordData: RecordData): Promise<RecordData> {
    return new Promise((resolve, reject) => {
      this.recordTableApiService.addNewRecord(path, recordData).subscribe({
        next: (recordData: RecordData) => resolve(recordData),
        error: (error) => reject(error)
      });
    });
  }

  protected updatedRecordField<T>(recordUpdateRequestParams: RecordUpdateRequestParams): Promise<T> {
    return new Promise((resolve, reject) => {
      this.recordTableApiService.updateRecord<T>(recordUpdateRequestParams).subscribe({
        next: (recordData: T) => resolve(recordData),
        error: (error) => reject(error)
      });
    });
  }

  protected updatedRecordData(recordUpdateRequestData: RecordUpdateRequestData): Promise<RecordData> {
    return new Promise((resolve, reject) => {
      this.baseModalApiService.updateRecordData(recordUpdateRequestData).subscribe({
        next: (recordData: RecordData) => resolve(recordData),
        error: (error) => reject(error)
      });
    });
  }

  protected fetchRecordPropertyDataById<T>(getRecordPropertyDataByIdParams: GetRecordPropertyDataByIdParams): Promise<T> {
    return new Promise((resolve, reject) => {
      this.baseModalApiService.fetchDataById<T>(getRecordPropertyDataByIdParams).subscribe({
        next: (recordPropertyData: T) => resolve(recordPropertyData),
        error: (error) => reject(error)
      });
    });
  }

  protected getRecordTableParams(path: string): Promise<Array<RecordTableColumnParams>> {
    return new Promise((resolve, reject) => {
      this.recordTableApiService.getRecordTableParams(path).subscribe({
        next: (recordTableParams: RecordTableParams) => resolve(recordTableParams.fields),
        error: (error) => reject(error)
      });
    });
  }

  protected deleteRecord(path: string, recordId: string | number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.recordTableApiService.deleteRecord(path, recordId).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  protected attachRecordData(attachProps: AttachProps): Promise<void> {
    return new Promise((resolve, reject) => {
      this.baseModalApiService.attachRecordData(attachProps).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  protected detachRecordData(detachProps: DetachProps): Promise<void> {
    return new Promise((resolve, reject) => {
      this.baseModalApiService.detachRecordData(detachProps).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  protected closeDialog(closeModalReason: CloseModalReason): void {
    this.dialogRef.close(closeModalReason);
  }

  protected preventInvalidNumericInput(event: KeyboardEvent, isDecimalPoint: boolean = false) {
    this.validationService.preventInvalidNumericInput(event, isDecimalPoint);
  }

  protected getIconStatus(iconStatus: IconStatus | undefined | null): IconStatus {
    return iconStatus ?? 'ERROR';
  }

  private isActiveLinkRowForUrlType(type: string): boolean {
    return !this.isEditModeActive && platformUrls.includes(type as PlatformUrlType);
  }

  private initEditModeSubscriptions(): void {
    this.subscriptions.add(
      this.editModeService.editMode$.subscribe({
        next: (isActive) => this.isEditModeActive = isActive
      })
    );
  }

  private initLoadingSubscriptions(): void {
    this.subscriptions.add(
      this.loadingService.loadingState$.subscribe({
        next: (isLoading: boolean) => this.isLoading = isLoading
      })
    );
  }
}
